import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck, Plus, Pencil, ChevronRight, ChevronDown, Loader2, Save,
  Code2, Ban, RotateCcw, GripVertical,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';

interface Regra {
  id: number;
  dominio: string;
  numero: string | null;
  titulo: string;
  regra: string;
  tecnico: string | null;
  da_ref: string | null;
  status: string;
  ordem: number;
  versao: number;
}

const DOMINIO_LABEL: Record<string, string> = {
  chamados: 'Chamados (Atendimento)',
};
function rotuloDominio(d: string): string {
  return DOMINIO_LABEL[d] ?? d.charAt(0).toUpperCase() + d.slice(1);
}
// Ordem fixa dos dominios (Chamados = 1). Dominio novo sem ordem explicita vai pro fim.
const DOMINIO_ORDER: Record<string, number> = { chamados: 1 };
function ordemDominio(d: string): number {
  return DOMINIO_ORDER[d] ?? 99;
}

interface Form {
  dominio: string;
  titulo: string;
  regra: string;
  tecnico: string;
  da_ref: string;
}
const FORM_VAZIO: Form = { dominio: 'chamados', titulo: '', regra: '', tecnico: '', da_ref: '' };

export function RDS() {
  const toast = useToast();
  const dialog = useDialog();
  const [regras, setRegras] = useState<Regra[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarRevogadas, setMostrarRevogadas] = useState(false);
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  const [tecnicos, setTecnicos] = useState<Set<number>>(new Set());

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Regra | null>(null);
  const [form, setForm] = useState<Form>(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const arrastandoRef = useRef<number | null>(null);

  async function carregar() {
    setLoading(true);
    try {
      const lista = await api.get<Regra[]>('/api/rds', { query: { revogadas: mostrarRevogadas ? '1' : undefined } });
      setRegras(lista);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, [mostrarRevogadas]);

  const porDominio = useMemo(() => {
    const m = new Map<string, Regra[]>();
    for (const r of regras) {
      if (!m.has(r.dominio)) m.set(r.dominio, []);
      m.get(r.dominio)!.push(r);
    }
    return [...m.entries()].sort((a, b) => ordemDominio(a[0]) - ordemDominio(b[0]) || a[0].localeCompare(b[0]));
  }, [regras]);

  function toggle(set: Set<number>, setSet: (s: Set<number>) => void, id: number) {
    const n = new Set(set);
    n.has(id) ? n.delete(id) : n.add(id);
    setSet(n);
  }

  function abrirNovo() {
    setEditando(null);
    setForm(FORM_VAZIO);
    setModalAberto(true);
  }
  function abrirEditar(r: Regra) {
    setEditando(r);
    setForm({ dominio: r.dominio, titulo: r.titulo, regra: r.regra, tecnico: r.tecnico ?? '', da_ref: r.da_ref ?? '' });
    setModalAberto(true);
  }

  async function salvar() {
    if (form.titulo.trim().length < 2) { toast.erro('Informe um título.'); return; }
    if (form.regra.trim().length < 2) { toast.erro('Descreva a regra.'); return; }
    setSalvando(true);
    try {
      const corpo = {
        dominio: form.dominio.trim() || 'chamados',
        titulo: form.titulo.trim(),
        regra: form.regra.trim(),
        tecnico: form.tecnico.trim() || null,
        da_ref: form.da_ref.trim() || null,
      };
      if (editando) {
        await api.patch(`/api/rds/${editando.id}`, corpo);
        toast.sucesso('Regra atualizada (versão anterior guardada no histórico).');
      } else {
        await api.post('/api/rds', corpo);
        toast.sucesso('Regra adicionada.');
      }
      setModalAberto(false);
      await carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function alternarStatus(r: Regra) {
    const revogar = r.status === 'ativa';
    const ok = await dialog.confirmar(
      revogar
        ? `Revogar a regra "${r.titulo}"? Ela sai da lista ativa (fica no histórico e visível com "Mostrar revogadas").`
        : `Reativar a regra "${r.titulo}"?`,
      { perigoso: revogar },
    );
    if (!ok) return;
    try {
      await api.patch(`/api/rds/${r.id}`, { status: revogar ? 'revogada' : 'ativa' });
      toast.sucesso(revogar ? 'Regra revogada.' : 'Regra reativada.');
      setModalAberto(false);
      await carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  // Arrasta a regra `origemId` pra posição da regra `alvoId`, dentro do mesmo domínio.
  async function reordenar(dominio: string, lista: Regra[], alvoId: number) {
    const origemId = arrastandoRef.current;
    arrastandoRef.current = null;
    if (origemId == null || origemId === alvoId) return;
    const ids = lista.map((r) => r.id);
    const from = ids.indexOf(origemId);
    const to = ids.indexOf(alvoId);
    if (from < 0 || to < 0) return;
    const [movido] = ids.splice(from, 1);
    ids.splice(to, 0, movido!);
    const novaOrdem = new Map(ids.map((id, i) => [id, i + 1]));
    setRegras((prev) =>
      [...prev]
        .map((r) => (r.dominio === dominio && novaOrdem.has(r.id) ? { ...r, ordem: novaOrdem.get(r.id)! } : r))
        .sort((a, b) => ordemDominio(a.dominio) - ordemDominio(b.dominio) || a.dominio.localeCompare(b.dominio) || a.ordem - b.ordem || a.id - b.id),
    );
    try {
      await api.patch('/api/rds/reordenar', { ids });
    } catch (e: any) {
      toast.erro('Não consegui salvar a nova ordem: ' + e.message);
      await carregar();
    }
  }

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pagina-titulo">
          <ShieldCheck size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          RDS — Regras de Diretriz do Sistema
        </h2>
        <button type="button" className="btn-primario" onClick={abrirNovo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} /> Adicionar / Atualizar Regra
        </button>
      </div>

      <div style={{ fontSize: 14, color: 'var(--page-fg)', marginBottom: 12, maxWidth: 760 }}>
        Cada cartão é uma regra do sistema em duas camadas: <b>a regra</b> (em linguagem de negócio) e o <b>técnico</b> (como funciona por baixo).
        Diretriz: nada do comportamento de chamado muda no sistema sem o cartão correspondente existir aqui primeiro.
      </div>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--text-muted)', marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={mostrarRevogadas} onChange={(e) => setMostrarRevogadas(e.target.checked)} />
        Mostrar revogadas
      </label>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> Carregando...
        </div>
      ) : regras.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--text-muted)' }}>Nenhuma regra cadastrada ainda.</div>
      ) : (
        porDominio.map(([dominio, lista], domIdx) => (
          <div key={dominio} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              <span style={{ opacity: 0.9 }}>{lista.find((x) => x.numero)?.numero?.split('.')[0] ?? String(domIdx + 1)}.</span> {rotuloDominio(dominio)} <span style={{ opacity: 0.6 }}>({lista.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lista.map((r) => {
                const numero = r.numero ?? '—';
                const aberto = abertos.has(r.id);
                const tecAberto = tecnicos.has(r.id);
                const revogada = r.status !== 'ativa';
                return (
                  <div
                    key={r.id}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => { e.preventDefault(); void reordenar(dominio, lista, r.id); }}
                    style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, opacity: revogada ? 0.6 : 1 }}
                  >
                    <div
                      draggable
                      onDragStart={(e) => { arrastandoRef.current = r.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(r.id)); }}
                      onDragEnd={() => { arrastandoRef.current = null; }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'grab' }}
                      onClick={() => toggle(abertos, setAbertos, r.id)}
                    >
                      <span title="Arraste pra reordenar" style={{ color: 'var(--text-muted)', display: 'inline-flex', flexShrink: 0, cursor: 'grab' }}><GripVertical size={16} /></span>
                      <span style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 40, color: 'var(--text-muted)', flexShrink: 0 }}>{numero}</span>
                      {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span style={{ fontSize: 16, fontWeight: 600, flex: 1, color: 'var(--primary)' }}>{r.titulo}</span>
                      {revogada && <span style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>REVOGADA</span>}
                      {r.versao > 1 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>v{r.versao}</span>}
                      {r.da_ref && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '1px 5px', borderRadius: 4 }}>{r.da_ref}</span>}
                      <button
                        type="button"
                        title="Atualizar regra"
                        onClick={(e) => { e.stopPropagation(); abrirEditar(r); }}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: '#fde68a', border: '1px solid #ca8a04', color: '#000', flexShrink: 0 }}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                    {aberto && (
                      <div style={{ padding: '0 12px 12px 36px' }}>
                        <div style={{ fontSize: 16, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{r.regra}</div>
                        {r.tecnico && (
                          <div style={{ marginTop: 10 }}>
                            <button
                              type="button"
                              onClick={() => toggle(tecnicos, setTecnicos, r.id)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text-muted)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <Code2 size={13} /> {tecAberto ? 'Ocultar técnico' : 'Ver técnico'}
                            </button>
                            {tecAberto && (
                              <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10, fontFamily: 'ui-monospace, monospace' }}>
                                {r.tecnico}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <Modal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        titulo={editando ? `Atualizar regra — ${editando.titulo}` : 'Adicionar regra'}
        largura={680}
        rodape={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8 }}>
            <div>
              {editando && (
                <button
                  type="button"
                  onClick={() => void alternarStatus(editando)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: editando.status === 'ativa' ? '#991b1b' : 'var(--text-muted)' }}
                >
                  {editando.status === 'ativa' ? <><Ban size={14} /> Revogar</> : <><RotateCcw size={14} /> Reativar</>}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setModalAberto(false)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent' }}>Cancelar</button>
              <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
              </button>
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {editando && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' }}>
              Ao salvar, a versão atual (v{editando.versao}) é guardada no histórico antes da alteração.
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Domínio</label>
              <input className="no-upper" value={form.dominio} onChange={(e) => setForm({ ...form, dominio: e.target.value })} placeholder="chamados" style={inp} />
            </div>
            <div style={{ width: 140 }}>
              <label style={lbl}>ADR (opcional)</label>
              <input className="no-upper" value={form.da_ref} onChange={(e) => setForm({ ...form, da_ref: e.target.value })} placeholder="DA-105" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Título</label>
            <input className="no-upper" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex: Como o robô escolhe a próxima atendente" style={inp} />
          </div>
          <div>
            <label style={lbl}>A regra (linguagem de negócio)</label>
            <textarea className="no-upper" value={form.regra} onChange={(e) => setForm({ ...form, regra: e.target.value })} rows={4} placeholder="O que a regra faz, do seu ponto de vista." style={{ ...inp, resize: 'vertical' }} />
          </div>
          <div>
            <label style={lbl}>Técnico (como funciona por baixo)</label>
            <textarea className="no-upper" value={form.tecnico} onChange={(e) => setForm({ ...form, tecnico: e.target.value })} rows={4} placeholder="Arquivos, lógica, decisão técnica — para o Claude." style={{ ...inp, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 14 }} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 15, background: 'var(--input-bg, #fff)' };
